import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, SkipForward, Sparkles } from 'lucide-react';
import * as Tone from 'tone';
import { fetchChatMessages } from '../shared/api/chat';
import { fetchJournalEntries } from '../shared/api/journal';
// ✅ Import centralized theme
import { gradients, buttonStyles, cardStyles, shadows } from '../shared/config/theme';
import { getJourneyConfig } from '../shared/constants/journeyConfig';

const MeditationSession = ({ 
  journeyData, 
  userId, 
  sessionId, 
  technique,
  onComplete, 
  onSkip
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(120);
  const [meditationScript, setMeditationScript] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  
  const synthRef = useRef(null);
  const intervalRef = useRef(null);
  const currentSegmentRef = useRef(0);

  // ✅ Get journey-specific styling
  const journeyStyle = getJourneyConfig(journeyData?.title || journeyData?.category);

  // Map journey titles to emojis
  const journeyEmojis = {
    'Confidence': '💪',
    'Love': '❤️',
    'Abundance': '✨',
    'Healing': '🌙',
    'Gratitude': '🙏',
    'Focus': '🎯',
    'Clarity': '💎',
    'Joy': '☀️',
    'Courage': '🦁'
  };

  useEffect(() => {
    generateMeditationScript();
    
    synthRef.current = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: 'sine' },
      envelope: {
        attack: 2,
        decay: 1,
        sustain: 0.4,
        release: 4,
      },
    }).toDestination();
    synthRef.current.volume.value = -20;

    return () => {
      if (synthRef.current) {
        synthRef.current.dispose();
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      speechSynthesis.cancel();
    };
  }, []);

  const generateMeditationScript = async () => {
    try {
      const { messages, error: chatError } = await fetchChatMessages(sessionId);
      if (chatError) throw chatError;

      const { entries, error: journalError } = await fetchJournalEntries(userId, { 
        sessionId, 
        limit: 1 
      });

      const journalData = entries && entries.length > 0 ? entries[0] : null;

      const themes = analyzeConversation(messages, journalData);
      
      const script = createPersonalizedScript(
        journeyData.title || journeyData.category,
        themes,
        technique
      );

      setMeditationScript(script);
      setDuration(calculateDuration(script));
      setLoading(false);

    } catch (error) {
      console.error('Error generating meditation script:', error);
      setMeditationScript(getDefaultScript(journeyData.title));
      setLoading(false);
    }
  };

  const analyzeConversation = (messages, journalData) => {
    const themes = {
      emotions: [],
      desires: [],
      challenges: [],
      strengths: []
    };

    messages?.forEach(msg => {
      if (msg.role === 'user') {
        const lower = msg.content.toLowerCase();
        
        if (/(anxious|worried|scared|fear)/.test(lower)) themes.emotions.push('anxiety');
        if (/(sad|depressed|down)/.test(lower)) themes.emotions.push('sadness');
        if (/(excited|happy|grateful)/.test(lower)) themes.emotions.push('joy');
        if (/(angry|frustrated|mad)/.test(lower)) themes.emotions.push('anger');
        
        if (/(want|need|desire|goal)/.test(lower)) {
          const match = lower.match(/(want|need|desire|goal)[^.!?]*/);
          if (match) themes.desires.push(match[0]);
        }
        
        if (/(struggle|difficult|hard|can't|unable)/.test(lower)) {
          themes.challenges.push('struggling');
        }
        
        if (/(can|will|able|strong|confident)/.test(lower)) {
          themes.strengths.push('determined');
        }
      }
    });

    if (journalData?.content) {
      const journalLower = journalData.content.toLowerCase();
      if (/(grateful|thankful|blessed)/.test(journalLower)) {
        themes.strengths.push('gratitude');
      }
    }

    return themes;
  };

  const createPersonalizedScript = (category, themes, technique) => {
    const scripts = {
      'Confidence': [
        { time: 0, text: `Welcome to your ${technique?.name || 'confidence'} meditation. Find a comfortable position and gently close your eyes.` },
        { time: 8, text: "Take a deep breath in... and slowly release. Feel your body relaxing with each exhale." },
        { time: 16, text: themes.challenges.length > 0 
          ? "I know you've been facing challenges. But right now, in this moment, you are safe and capable."
          : "You are safe, you are capable, and you are exactly where you need to be." },
        { time: 25, text: "Bring your awareness to your heart center. Feel the power and confidence that already exists within you." },
        { time: 34, text: "With each breath, you're becoming more aligned with your true, confident self." },
        { time: 44, text: themes.desires.length > 0
          ? "Visualize yourself achieving what you desire. See it clearly. Feel how it feels to embody that confidence."
          : "See yourself walking through life with ease and certainty. You trust yourself completely." },
        { time: 54, text: "This confidence isn't something you need to find. It's something you already are." },
        { time: 63, text: "Breathe into this feeling. Let it fill every cell of your body." },
        { time: 72, text: "You are worthy. You are powerful. You are confident." },
        { time: 81, text: "Take three more deep breaths, anchoring this feeling into your being." },
        { time: 90, text: "When you're ready, gently open your eyes, carrying this confidence with you." }
      ],
      'Love': [
        { time: 0, text: `Welcome to your ${technique?.name || 'love'} meditation. Settle into a comfortable space and close your eyes.` },
        { time: 8, text: "Take a deep breath in through your nose... and gently exhale through your mouth." },
        { time: 16, text: "Place your hand over your heart. Feel its gentle rhythm." },
        { time: 24, text: themes.emotions.includes('sadness')
          ? "I know your heart has felt heavy. But love is always here, waiting to heal you."
          : "Your heart is a source of infinite love and compassion." },
        { time: 33, text: "With each breath, feel warmth spreading from your heart throughout your entire body." },
        { time: 42, text: "You are deserving of love. Not because of what you do, but because of who you are." },
        { time: 51, text: themes.desires.length > 0
          ? "The love you seek is already within you. Feel it growing stronger with each moment."
          : "Feel the love that surrounds you from the universe, from those who care about you, from yourself." },
        { time: 61, text: "Breathe in love. Breathe out anything that blocks it." },
        { time: 70, text: "You are love. You are loved. You are lovable." },
        { time: 79, text: "Take a few more breaths, letting this love fill you completely." },
        { time: 88, text: "Gently open your eyes when you're ready, carrying this love with you today." }
      ],
      'Abundance': [
        { time: 0, text: `Welcome to your ${technique?.name || 'abundance'} meditation. Get comfortable and close your eyes.` },
        { time: 8, text: "Take a deep, nourishing breath in... and release completely." },
        { time: 16, text: "Let go of any tension. You are entering a space of limitless possibility." },
        { time: 25, text: themes.challenges.includes('struggling')
          ? "I know you've felt scarcity. But abundance is your natural state. You are ready to receive."
          : "Abundance flows to you easily and naturally. You are a magnet for prosperity." },
        { time: 35, text: "Visualize golden light surrounding you. This is the energy of abundance." },
        { time: 44, text: "Feel this light entering your body with each inhale. You are being filled with prosperity." },
        { time: 54, text: themes.desires.length > 0
          ? "See your desires manifesting. They are coming to you now. You are worthy of receiving them."
          : "The universe wants to give to you. You are worthy of all good things." },
        { time: 64, text: "Breathe in abundance. Breathe out limitation." },
        { time: 73, text: "You are abundant. You are prosperous. You are wealthy in all ways." },
        { time: 82, text: "Take three final breaths, anchoring this abundance into your reality." },
        { time: 91, text: "Open your eyes when ready, knowing abundance flows to you now." }
      ],
      'Healing': [
        { time: 0, text: `Welcome to your ${technique?.name || 'healing'} meditation. Find your peaceful center and close your eyes.` },
        { time: 8, text: "Breathe in healing energy... breathe out what no longer serves you." },
        { time: 17, text: themes.emotions.includes('sadness') || themes.challenges.length > 0
          ? "Your body knows how to heal. Your heart knows how to mend. Trust the process."
          : "You are whole. You are healing. You are renewed." },
        { time: 26, text: "Scan your body from head to toe. Send love and healing light to any area that needs it." },
        { time: 36, text: "With each breath, feel healing energy flowing through you like a gentle stream." },
        { time: 46, text: "Release any pain, any hurt, any discomfort. Let it dissolve into the light." },
        { time: 56, text: "You are safe. You are supported. You are healing perfectly." },
        { time: 66, text: "Feel gratitude for your body's wisdom and its natural ability to restore balance." },
        { time: 75, text: "Breathe deeply, allowing complete healing on all levels - body, mind, and spirit." },
        { time: 84, text: "Take three more healing breaths, sealing this energy within you." },
        { time: 93, text: "Open your eyes gently, feeling refreshed, renewed, and healed." }
      ],
      'Gratitude': [
        { time: 0, text: `Welcome to your ${technique?.name || 'gratitude'} meditation. Settle in and close your eyes with a gentle smile.` },
        { time: 8, text: "Take a deep breath in... and release with appreciation." },
        { time: 16, text: "Bring to mind something simple you're grateful for. Feel that warmth in your heart." },
        { time: 25, text: themes.strengths.includes('gratitude')
          ? "Your grateful heart is a magnet for blessings. Feel how rich your life already is."
          : "Look around your life with new eyes. See all the gifts that surround you." },
        { time: 35, text: "Think of a person who has touched your life. Send them silent thanks." },
        { time: 44, text: "Feel gratitude for your body, carrying you through each day." },
        { time: 53, text: "Appreciate the simple things - breath, sunlight, water, shelter." },
        { time: 62, text: "Even challenges have taught you and made you stronger. Thank them too." },
        { time: 71, text: "Let gratitude fill every cell of your being. You are so blessed." },
        { time: 80, text: "Take three grateful breaths, anchoring this appreciation into your heart." },
        { time: 89, text: "Open your eyes, seeing your world through the lens of gratitude." }
      ],
      'Focus': [
        { time: 0, text: `Welcome to your ${technique?.name || 'focus'} meditation. Find your center and close your eyes.` },
        { time: 8, text: "Take a clearing breath in... and release all distractions." },
        { time: 16, text: "Imagine your mind becoming still like a calm lake." },
        { time: 25, text: themes.challenges.length > 0
          ? "I know your mind has felt scattered. But clarity is your natural state. You can access it now."
          : "Your mind is powerful and clear. You can focus on what matters most." },
        { time: 35, text: "With each breath, unnecessary thoughts drift away like clouds." },
        { time: 44, text: "Feel your mental energy gathering, becoming sharp and precise." },
        { time: 53, text: themes.desires.length > 0
          ? "See your goals clearly. Your focused mind will manifest them into reality."
          : "You have the power to direct your attention exactly where you choose." },
        { time: 63, text: "Distractions have no power over you. You are centered and clear." },
        { time: 72, text: "Breathe in clarity. Breathe out confusion." },
        { time: 81, text: "Take three final breaths, anchoring this focused state." },
        { time: 90, text: "Open your eyes with laser-like focus, ready to direct your energy wisely." }
      ],
      'Clarity': [
        { time: 0, text: `Welcome to your ${technique?.name || 'clarity'} meditation. Get comfortable and close your eyes.` },
        { time: 8, text: "Breathe in deeply... and exhale any mental fog." },
        { time: 16, text: "Imagine a bright light clearing away confusion from your mind." },
        { time: 25, text: themes.challenges.includes('struggling')
          ? "The answers you seek are already within you. Let them rise to the surface."
          : "You have access to infinite wisdom. Trust what you know." },
        { time: 35, text: "With each breath, your mind becomes clearer, like wiping mist from a mirror." },
        { time: 44, text: "You see your path clearly. You know what to do." },
        { time: 53, text: "Release the need to overthink. The truth is simple and clear." },
        { time: 62, text: themes.desires.length > 0
          ? "You can see your next steps clearly. Trust your inner knowing."
          : "You have clarity. You have wisdom. You know your truth." },
        { time: 72, text: "Breathe in clarity. Breathe out doubt." },
        { time: 81, text: "Take three more breaths, anchoring this crystal-clear awareness." },
        { time: 90, text: "Open your eyes, seeing everything with perfect clarity." }
      ],
      'Joy': [
        { time: 0, text: `Welcome to your ${technique?.name || 'joy'} meditation. Settle in with a smile and close your eyes.` },
        { time: 8, text: "Take a delightful breath in... and release with a sigh of contentment." },
        { time: 17, text: "Feel a spark of joy igniting in your heart center." },
        { time: 26, text: themes.emotions.includes('sadness')
          ? "Joy is your birthright. It's okay to feel it, even now. Let it in."
          : "You are a being of pure joy. Let it bubble up naturally from within." },
        { time: 36, text: "With each breath, this joy grows warmer and brighter." },
        { time: 45, text: "Remember a moment when you felt truly happy. Feel that feeling now." },
        { time: 54, text: "Joy doesn't need a reason. It's your natural state. Claim it." },
        { time: 63, text: "Let joy spread through your entire body like sunshine." },
        { time: 72, text: "You are joy. You are light. You are alive." },
        { time: 81, text: "Take three joyful breaths, anchoring this lightness in your being." },
        { time: 90, text: "Open your eyes with a smile, carrying this joy into your day." }
      ],
      'Courage': [
        { time: 0, text: `Welcome to your ${technique?.name || 'courage'} meditation. Stand tall in your power and close your eyes.` },
        { time: 8, text: "Take a bold breath in... and release with strength." },
        { time: 16, text: "Feel courage rising from deep within you like a powerful current." },
        { time: 25, text: themes.challenges.length > 0
          ? "You've already survived so much. That's proof of your courage. Feel it now."
          : "You are braver than you know. Courage is your essence." },
        { time: 35, text: "With each breath, you're connecting to the warrior spirit within you." },
        { time: 44, text: "Fear may visit, but courage is your home. You are safe here." },
        { time: 53, text: themes.desires.length > 0
          ? "You have the courage to pursue your dreams. Nothing can stop you."
          : "You face life boldly. You trust yourself completely." },
        { time: 63, text: "Feel your spine straighten. Your heart opens. Your courage solidifies." },
        { time: 72, text: "Breathe in courage. Breathe out fear." },
        { time: 81, text: "Take three powerful breaths, anchoring this courage into your bones." },
        { time: 90, text: "Open your eyes as the courageous being you truly are." }
      ]
    };

    return scripts[category] || scripts['Confidence'];
  };

  const getDefaultScript = (category) => {
    return [
      { time: 0, text: `Welcome to your ${category} meditation. Close your eyes and breathe deeply.` },
      { time: 10, text: "Allow yourself to relax completely." },
      { time: 20, text: "Focus on your breath, in and out." },
      { time: 30, text: "Feel peace washing over you." },
      { time: 40, text: "You are exactly where you need to be." },
      { time: 50, text: "Continue breathing deeply." },
      { time: 60, text: "Feel gratitude for this moment." },
      { time: 70, text: "When you're ready, gently open your eyes." }
    ];
  };

  const calculateDuration = (script) => {
    if (!script || script.length === 0) return 120;
    const lastSegment = script[script.length - 1];
    return lastSegment.time + 10;
  };

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.85;
      utterance.pitch = 1;
      utterance.volume = 0.9;
      
      const voices = speechSynthesis.getVoices();
      const preferredVoice = voices.find(voice => 
        voice.name.includes('Female') || voice.name.includes('Samantha')
      );
      if (preferredVoice) utterance.voice = preferredVoice;
      
      speechSynthesis.speak(utterance);
    }
  };

  const playAmbientSound = () => {
    if (synthRef.current && Tone.context.state === 'running') {
      const notes = ['C4', 'E4', 'G4', 'B4'];
      const randomNote = notes[Math.floor(Math.random() * notes.length)];
      synthRef.current.triggerAttackRelease(randomNote, '4n');
    }
  };

  const handlePlayPause = async () => {
    if (!isPlaying && Tone.context.state !== 'running') {
      await Tone.start();
    }
    
    setIsPlaying(!isPlaying);
    
    if (!isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentTime(prevTime => {
          const newTime = prevTime + 1;
          
          const currentSegment = meditationScript.find(
            (seg, idx) => seg.time === newTime && idx > currentSegmentRef.current
          );
          
          if (currentSegment) {
            const segmentIndex = meditationScript.indexOf(currentSegment);
            setCurrentSegmentIndex(segmentIndex);
            currentSegmentRef.current = segmentIndex;
            speakText(currentSegment.text);
            
            if (newTime % 20 === 0) {
              playAmbientSound();
            }
          }
          
          if (newTime >= duration) {
            setIsPlaying(false);
            clearInterval(intervalRef.current);
            setTimeout(onComplete, 1000);
            return duration;
          }
          
          return newTime;
        });
      }, 1000);
    } else {
      setIsPlaying(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      speechSynthesis.cancel();
    }
  };

  const handleReset = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    setCurrentSegmentIndex(0);
    currentSegmentRef.current = 0;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    speechSynthesis.cancel();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = (currentTime / duration) * 100;

  if (loading) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${gradients.background} flex items-center justify-center`}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium text-xl">Preparing your personalized meditation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${gradients.background} flex items-center justify-center p-4`}>
      <div className={`max-w-md w-full ${cardStyles.soft.background} ${cardStyles.soft.border} ${cardStyles.soft.rounded} ${cardStyles.soft.shadow} p-8`}>
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">
            {journeyEmojis[journeyData.title] || journeyStyle.emoji}
          </div>
          <h1 className="text-gray-900 text-3xl font-bold mb-2">
            {journeyData.title} Meditation
          </h1>
          <p className={`${journeyStyle.textColor}`}>
            {technique ? `${technique.name} Practice` : 'Guided meditation based on your journey'}
          </p>
        </div>

        <div className="mb-8">
          {/* Progress Circle */}
          <div className="relative w-48 h-48 mx-auto mb-6">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="96"
                cy="96"
                r="88"
                stroke="rgba(0, 0, 0, 0.1)"
                strokeWidth="8"
                fill="none"
              />
              <circle
                cx="96"
                cy="96"
                r="88"
                stroke="url(#gradient)"
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 88}`}
                strokeDashoffset={`${2 * Math.PI * 88 * (1 - progress / 100)}`}
                strokeLinecap="round"
                className="transition-all duration-1000 ease-linear"
              />
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FF9966" />
                  <stop offset="50%" stopColor="#FF7A45" />
                  <stop offset="100%" stopColor="#F97316" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-gray-900 text-4xl font-bold">
                  {formatTime(currentTime)}
                </div>
                <div className="text-gray-600 text-sm mt-1">
                  of {formatTime(duration)}
                </div>
              </div>
            </div>
          </div>

          {/* Control Buttons */}
          <div className="flex justify-center gap-4 mb-6">
            <button
              onClick={handlePlayPause}
              className={`${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} rounded-full p-4 ${shadows.lg}`}
            >
              {isPlaying ? <Pause size={32} /> : <Play size={32} className="ml-1" />}
            </button>
            <button
              onClick={handleReset}
              className={`${buttonStyles.soft.base} ${buttonStyles.soft.hover} ${buttonStyles.soft.active} ${buttonStyles.soft.transition} rounded-full p-4 ${shadows.md}`}
            >
              <RotateCcw size={32} />
            </button>
          </div>

          {/* Skip Button */}
          <button
            onClick={onSkip}
            className={`w-full ${buttonStyles.secondary.base} ${buttonStyles.secondary.hover} ${buttonStyles.secondary.active} ${buttonStyles.secondary.transition} ${buttonStyles.secondary.rounded} ${buttonStyles.secondary.shadow} py-3 px-6 flex items-center justify-center gap-2`}
          >
            Skip to Affirmations
            <SkipForward size={20} />
          </button>
        </div>

        {/* Info Card */}
        <div className={`${cardStyles.default.background} ${cardStyles.default.border} ${cardStyles.default.rounded} p-6`}>
          <div className="flex items-start gap-3 mb-4">
            <Sparkles className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-gray-900 font-semibold mb-2">Meditation Guide</h3>
              <p className="text-gray-600 text-sm mb-3">
                This meditation was personalized based on your conversation and journal entry.
              </p>
            </div>
          </div>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start">
              <span className="text-orange-500 mr-2">•</span>
              <span>Find a quiet, comfortable space</span>
            </li>
            <li className="flex items-start">
              <span className="text-peach-500 mr-2">•</span>
              <span>Use headphones for the best experience</span>
            </li>
            <li className="flex items-start">
              <span className="text-orange-400 mr-2">•</span>
              <span>Close your eyes and follow the guidance</span>
            </li>
            <li className="flex items-start">
              <span className="text-cream-600 mr-2">•</span>
              <span>Allow yourself to fully relax and receive</span>
            </li>
          </ul>
        </div>

        {/* Current Segment Display */}
        {currentSegmentIndex > 0 && currentSegmentIndex < meditationScript.length && (
          <div className="mt-6 text-center">
            <p className="text-gray-600 text-sm italic leading-relaxed">
              "{meditationScript[currentSegmentIndex].text}"
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MeditationSession;